import { CircularProgress, makeStyles, Typography } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import { useSnackbar } from 'notistack';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Field, Form, FormRenderProps, useForm } from 'react-final-form';
import { useLocation, useParams } from 'react-router-dom';
import { Mentor, MentorDetails, MentorStatus } from '../../api/mentors';
import { Specialization } from '../../api/specializations';
import { Role } from '../../api/user/Role';
import { useResourceBundles } from '../../contexts/resource-bundles-context';
import { UserContext } from '../../contexts/user-context';
import { useScrollOnValidation } from '../../hooks/useScrollOnValidation';
import {
  apply,
  Either,
  isLeft,
  left,
  parseObject,
  right,
} from '../../utils/Either';
import { Email, stringToEitherEmail } from '../../utils/String/Email';
import { Phone, stringToEitherPhone } from '../../utils/String/Phone';
import { fromString, Sentence } from '../../utils/String/Sentence';
import { isUrl } from '../../utils/String/Url';
import { stringToEitherZip, Zip } from '../../utils/String/Zip';
import { formatDateToRFC } from '../../utils/date';
import {
  PastStringDate,
  strToEitherPastStrDate,
} from '../../utils/date/PastStringDate';
import {
  GENDER_MENTOR,
  lengthField,
  RACE,
  RADIO_SELECT,
  SENIORITY_OPTIONS,
  STATUSES_MENTORS,
} from '../../utils/form';
import {
  checkSizeTablet,
  getAbbreviationByName,
  isMobile,
} from '../../utils/functions';
import { CLASS_TRACKING } from '../../utils/tracking_class';
import {
  ActivateConfirmationModal,
  BeforeUnload,
  Button,
  FieldNames,
  FormGroup,
  StickyContent,
  UserLogo,
} from '../common';
import { hasDuplicateEmail } from '../common/field-email';
import { checkNameForDublicates } from '../common/field-names';
import {
  FormComboBox,
  FormDateInput,
  FormEmail,
  FormSelect,
  FormSpecializationInput,
  NumberField,
  TextFieldWrapper,
} from './wrappers';

interface MentorDetailsFormProps {
  mentorId?: Mentor['id'];
  mentorDetails?: MentorDetails;
  mentorSpecializations?: Specialization['id'][];
  loading?: boolean;
  onSubmit: (parsedFormValues: ParsedFormValues) => any;
  isExternal?: boolean;
}

interface FormValues {
  address: null | string | Sentence<1024>;
  city: Sentence<250> | string | null;
  country: Sentence<250> | string | null;
  dateOfBirth: PastStringDate | string | null;
  dateOfEnrollment: PastStringDate | string | null;
  email: Email | string | null;
  firstName: Sentence<250> | string;
  lastName: Sentence<250> | string;
  about: Sentence<8000> | string | null;
  linkedInProfile: Sentence<250> | string | null;
  logo: Sentence<250> | string | null;
  phone: Phone | string | null;
  specializations: Specialization['id'][];
  state: Sentence<250> | string | null;
  status: MentorStatus;
  verifiedEmail: boolean;
  verifiedName: boolean;
  zip: Zip | string | null;
  gender: string | Sentence<20> | null;
  race: string | Sentence<50> | null;
  currentRole: Sentence<255> | string | null;
  companyName: Sentence<255> | string | null;
  seniority: Sentence<255> | string | null;
  experienceYears: number | string | null;
  isEnterpreneur: 'true' | 'false';
  isInvestor: 'true' | 'false';
  skills: Sentence<4096> | string | null;
  mentorCapacity: number | string | null;
}

interface ValidFormValues extends FormValues {
  address: Sentence<1024> | null;
  city: Sentence<250> | null;
  country: Sentence<250> | null;
  dateOfBirth: PastStringDate | null;
  dateOfEnrollment: PastStringDate | null;
  email: Email;
  firstName: Sentence<250>;
  lastName: Sentence<250>;
  about: Sentence<8000>;
  linkedInProfile: Sentence<250> | null;
  logo: Sentence<250> | null;
  phone: Phone | null;
  specializations: Specialization['id'][];
  state: Sentence<250> | null;
  status: MentorStatus;
  verifiedEmail: boolean;
  verifiedName: boolean;
  zip: Zip | null;
  gender: Sentence<20> | null;
  race: Sentence<50> | null;
  currentRole: Sentence<255> | null;
  companyName: Sentence<255> | null;
  seniority: Sentence<255> | null;
  experienceYears: number | null;
  isEnterpreneur: 'true' | 'false';
  isInvestor: 'true' | 'false';
  skills: Sentence<4096> | null;
  mentorCapacity: number;
}

interface MentorDetailsValues
  extends Omit<MentorDetails, 'mentorAdditionalInfo' | 'dateOfEnrollment'> {
  dateOfEnrollment: PastStringDate | null;
  mentorAdditionalInfo: {
    gender: Sentence<20> | null;
    race: Sentence<50> | null;
  };
}

export interface ParsedFormValues {
  values: MentorDetailsValues;
  specializations: Specialization['id'][];
  logoFile: File | null;
}

const isEnrollmentDateRequired = (status?: MentorStatus) =>
  (status ?? MentorStatus.ACTIVE) === MentorStatus.ACTIVE;

const useStyles = makeStyles((theme) => ({
  sectionBlock: {
    '& + &': {
      marginTop: 56,
    },
  },
  sectionTitle: {
    marginBottom: 32,
  },
  formBlocks: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  logoFormBlock: {
    margin: '0 0 32px 0',

    [theme.breakpoints.up('xs')]: {
      margin: '65px 0 0 30px',
    },

    [theme.breakpoints.up(1000)]: {
      marginLeft: 80,
    },
  },
  mainFormBlock: {
    width: '100%',

    [theme.breakpoints.up('xs')]: {
      width: 560,
    },
  },
  actionsBlock: {
    marginTop: 56,
  },
  statusField: {
    marginTop: 0,

    [theme.breakpoints.up('xs')]: {
      marginTop: 15,
    },
  },
  aboutField: {
    minHeight: 64,
  },
}));

function getValidForm(
  form: Partial<FormValues>,
): Either<Partial<Record<keyof FormValues, string>>, ValidFormValues> {
  return parseObject<
    Partial<FormValues>,
    ValidFormValues,
    Partial<Record<keyof FormValues, string>>
  >(
    {
      address: (v) => (v ? fromString(1024, v) : right(null)),
      city: (v) => (v ? fromString(250, v) : right(null)),
      country: (v) => (v ? fromString(250, v) : right(null)),
      dateOfBirth: (v) => (v ? strToEitherPastStrDate(v) : right(null)),
      dateOfEnrollment: (v) =>
        !v && !isEnrollmentDateRequired(form.status)
          ? right(null)
          : v
            ? strToEitherPastStrDate(v)
            : left('Invalid date'),
      email: (v) => (v ? stringToEitherEmail(v) : left('Required')),
      firstName: (v) => fromString(250, v ?? ''),
      lastName: (v) => fromString(250, v ?? ''),
      about: (v) => fromString(8000, v ?? ''),
      linkedInProfile: (v) =>
        v ? (isUrl(v) ? fromString(250, v) : left('Invalid URL')) : right(null),
      logo: (v) => (v ? fromString(250, v) : right(null)),
      phone: (v) => (v ? stringToEitherPhone(v) : right(null)),
      specializations: (v) => right(v ?? []),
      state: (v) => (v ? fromString(250, v) : right(null)),
      status: (v) => right(v ?? MentorStatus.ACTIVE),
      verifiedEmail: (v) => right(v ?? false),
      verifiedName: (v) => right(v ?? false),
      zip: (v) => (v ? stringToEitherZip(v) : right(null)),
      gender: (v) => (v ? fromString(20, v) : right(null)),
      race: (v) => (v ? fromString(50, v) : right(null)),
      currentRole: (v) => (v ? fromString(255, v) : right(null)),
      companyName: (v) => (v ? fromString(255, v) : right(null)),
      seniority: (v) => (v ? fromString(255, v) : right(null)),
      experienceYears: (v) => {
        if (v === '' || v === null || v === undefined) return right(null);
        const num = Number(v);
        if (!Number.isInteger(num) || num < 0 || num > 99)
          return left('Must be an integer between 0 and 99');
        return right(num);
      },
      isEnterpreneur: (v) => right(v ?? 'false'),
      isInvestor: (v) => right(v ?? 'false'),
      skills: (v) => (v ? fromString(4096, v) : right(null)),
      mentorCapacity: (v) => {
        if (v === '' || v === null || v === undefined) return left('Required');
        const num = Number(v);
        if (!Number.isInteger(num) || num < 0 || num > 10)
          return left('Must be an integer between 0 and 10');
        return right(num);
      },
    },
    form,
  );
}

const validateForm = (
  values: FormValues,
): { [k in keyof FormValues]?: string } => {
  const validForm = getValidForm(values);

  return isLeft(validForm) ? validForm.value : {};
};

function getInitialValues(
  mentorDetails?: MentorDetails,
  specializations: Specialization['id'][] = [],
): FormValues {
  const country = mentorDetails?.country || '';

  return {
    address: mentorDetails?.address || '',
    firstName: mentorDetails?.firstName || '',
    lastName: mentorDetails?.lastName || '',
    verifiedName: !!(mentorDetails?.firstName && mentorDetails.lastName),
    dateOfBirth: mentorDetails?.dateOfBirth || '',
    dateOfEnrollment: mentorDetails?.dateOfEnrollment || '',
    specializations,
    about: mentorDetails?.about || '',
    email: mentorDetails?.email || '',
    verifiedEmail: !!mentorDetails?.email,
    phone: mentorDetails?.phone || '',
    linkedInProfile: mentorDetails?.linkedInProfile || '',
    country,
    state: mentorDetails?.state || '',
    city: mentorDetails?.city || '',
    logo: mentorDetails?.logo || '',
    zip: mentorDetails?.zip || '',
    status: mentorDetails?.status || STATUSES_MENTORS[2].value,
    gender: mentorDetails?.mentorAdditionalInfo?.gender || '',
    race: mentorDetails?.mentorAdditionalInfo?.race || '',
    currentRole: mentorDetails?.currentRole || '',
    companyName: mentorDetails?.companyName || '',
    seniority: mentorDetails?.seniority || '',
    experienceYears:
      mentorDetails?.experienceYears != null
        ? String(mentorDetails.experienceYears)
        : '',
    isEnterpreneur: mentorDetails?.isEnterpreneur ? 'true' : 'false',
    isInvestor: mentorDetails?.isInvestor ? 'true' : 'false',
    skills: mentorDetails?.skills || '',
    mentorCapacity:
      mentorDetails?.mentorCapacity != null
        ? String(mentorDetails.mentorCapacity)
        : '2',
  };
}

function getParsedValues(
  formValues: FormValues,
  logoFile: File | null,
): Either<undefined, ParsedFormValues> {
  return apply(
    () => left(undefined),
    (formValues) =>
      right({
        values: {
          address: formValues.address,
          firstName: formValues.firstName,
          lastName: formValues.lastName,
          about: formValues.about,
          dateOfBirth: formValues.dateOfBirth,
          dateOfEnrollment: formValues.dateOfEnrollment || null,
          email: formValues.email,
          phone: formValues.phone,
          linkedInProfile: formValues.linkedInProfile,
          country: formValues.country,
          state: formValues.state,
          city: formValues.city,
          logo: formValues.logo,
          zip: formValues.zip,
          status: formValues.status,
          currentRole: formValues.currentRole,
          companyName: formValues.companyName,
          seniority: formValues.seniority,
          experienceYears: formValues.experienceYears,
          isEnterpreneur: formValues.isEnterpreneur === 'true',
          isInvestor: formValues.isInvestor === 'true',
          skills: formValues.skills,
          mentorCapacity: formValues.mentorCapacity,
          mentorAdditionalInfo: {
            gender: formValues.gender,
            race: formValues.race,
          },
        },
        specializations: formValues.specializations,
        logoFile,
      }),
    getValidForm(formValues),
  );
}

function parseEmptyString(value: string) {
  return value === '' ? '' : value;
}

function MentorDetailsForm({
  mentorDetails,
  mentorSpecializations,
  loading = false,
  onSubmit,
  isExternal = false,
}: MentorDetailsFormProps) {
  const {
    identityid,
    hasAccessToAction: checkAccess,
    hasRole,
  } = useContext(UserContext);
  const hasAccessToAction = useMemo(
    () => (isExternal ? () => true : checkAccess),
    [checkAccess, isExternal],
  );
  const { rb } = useResourceBundles();
  const classes = useStyles();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { mentorId } = useParams<{ mentorId: string }>();
  const setSubmitValidationFailed = useScrollOnValidation();

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [isAsyncValidating, setIsAsyncValidating] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] =
    useState<MentorStatus | null>(null);

  const hasAccessToUpdateAdditionalInfo = useMemo(() => {
    const isExternalPage =
      location.pathname.includes('external') || identityid === mentorId;
    return isExternalPage
      ? true
      : hasAccessToAction('mentor.additionalInfo.update');
  }, [hasAccessToAction, identityid, location.pathname, mentorId]);

  const handleSubmit = async (formValues: FormValues) => {
    if (isAsyncValidating) {
      return;
    }

    const parsedFormValues = getParsedValues(formValues, logoFile);

    if (isLeft(parsedFormValues)) {
      return;
    }

    const values = parsedFormValues.value.values;

    try {
      setIsAsyncValidating(true);

      const isEmailDublicate =
        values.email !== mentorDetails?.email &&
        (await hasDuplicateEmail('mentor', values.email));

      if (isEmailDublicate) {
        throw new Error('Email is dublicate');
      }

      const nameCheckStatus =
        values.firstName === mentorDetails?.firstName &&
          values.lastName === mentorDetails?.lastName
          ? 'success'
          : await checkNameForDublicates(
            'mentor',
            values.firstName,
            values.lastName,
          );

      if (nameCheckStatus !== 'success') {
        throw new Error('Name is dublicate');
      }

      setIsAsyncValidating(false);
    } catch (error) {
      setIsAsyncValidating(false);
      return;
    }

    return onSubmit(parsedFormValues.value);
  };

  const initialValues = useMemo(
    () => getInitialValues(mentorDetails, mentorSpecializations),
    [mentorDetails, mentorSpecializations],
  );

  const formAPIRef = useRef<any>(null);

  const handleStatusChange = (
    newStatus: MentorStatus,
    currentStatus: MentorStatus,
  ) => {
    if (
      initialValues.status === MentorStatus.APPLICANT &&
      newStatus === MentorStatus.ACTIVE
    ) {
      setPendingStatusChange(MentorStatus.ACTIVE);
      return currentStatus;
    }
    if (
      newStatus === MentorStatus.ACTIVE &&
      formAPIRef.current &&
      !formAPIRef.current.getState().values.dateOfEnrollment
    ) {
      formAPIRef.current.change('dateOfEnrollment', formatDateToRFC(new Date()));
    }
    return newStatus;
  };

  const handleActivateConfirm = () => {
    if (pendingStatusChange && formAPIRef.current) {
      formAPIRef.current.change('status', pendingStatusChange);
      if (!formAPIRef.current.getState().values.dateOfEnrollment) {
        formAPIRef.current.change('dateOfEnrollment', formatDateToRFC(new Date()));
      }
    }
    setPendingStatusChange(null);
  };

  const handleActivateCancel = () => {
    setPendingStatusChange(null);
  };

  const validateLogoFile = (logo: File) => {
    const fileName = logo.name;
    if (fileName.length > lengthField.logoFileName) {
      enqueueSnackbar('The maximum length of the file name is 75 characters', {
        variant: 'error',
      });
      return false;
    }
    return true;
  };

  return (
    <div>
      <Form
        validate={validateForm}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        keepDirtyOnReinitialize
        render={(formProps) => {
          setSubmitValidationFailed(
            formProps.submitFailed &&
            !formProps.dirtySinceLastSubmit &&
            !formProps.submitting,
          );
          return (
            <FormWrapper formProps={formProps} formAPIRef={formAPIRef}>
              <BeforeUnload
                when={formProps.dirty && !loading}
                title='Leave the page'
                body='You are about to leave the page, all unsaved changes will be lost. Do you want to continue?'
                disabled={loading}
                confirmButtonRenderer={({ onConfirm }) => (
                  <Button
                    variant='outlined'
                    onClick={async () => {
                      await formProps.handleSubmit();
                      onConfirm();
                    }}
                    disabled={loading || !formProps.valid}
                  >
                    {loading ? (
                      <CircularProgress size={24} color='inherit' />
                    ) : (
                      'Save the changes'
                    )}
                  </Button>
                )}
              />
              <div
                className={classes.formBlocks}
                data-testid='mentor-details-form'
              >
                <div className={classes.mainFormBlock}>
                  <form noValidate>
                    <div className={classes.sectionBlock}>
                      {isMobile() && (
                        <div className={classes.logoFormBlock}>
                          <Field<string>
                            name='logo'
                            parse={(value) => (!value ? '' : value)}
                            component={({ input }) => (
                              <UserLogo
                                logo={input.value}
                                name={getAbbreviationByName(
                                  formProps.values.firstName,
                                  formProps.values.lastName,
                                )}
                                onUpload={(photoFile) => {
                                  if (validateLogoFile(photoFile)) {
                                    setLogoFile(photoFile);
                                    const blobFile =
                                      URL.createObjectURL(photoFile);
                                    input.onChange(blobFile);
                                  }
                                }}
                                onRemove={() => {
                                  setLogoFile(null);
                                  input.onChange('');
                                }}
                                readOnly={
                                  !hasAccessToAction('mentor.details.update')
                                }
                              />
                            )}
                          />
                        </div>
                      )}

                      {hasAccessToAction('mentor.details.field.firstName') &&
                        hasAccessToAction('mentor.details.field.lastName') && (
                          <FieldNames
                            entityType='mentor'
                            isReadOnly={
                              !hasAccessToAction('mentor.details.update')
                            }
                          />
                        )}
                      {hasAccessToAction('mentor.details.field.dateOfBirth') &&
                        hasAccessToAction(
                          'mentor.details.field.dateOfEnrollment',
                        ) && (
                          <FormGroup mobile={checkSizeTablet(800)}>
                            <Field<string>
                              testid='mentor-details-date-birth'
                              name='dateOfBirth'
                              component={FormDateInput}
                              label='Date of birth'
                              editable
                              disableFuture
                              parse={(value) => {
                                try {
                                  if (value) {
                                    return formatDateToRFC(value);
                                  }
                                  return '';
                                } catch (e: any) {
                                  return 'invalid';
                                }
                              }}
                              isReadOnly={
                                !hasAccessToAction('mentor.details.update')
                              }
                            />
                            <Field<string>
                              name='dateOfEnrollment'
                              testid='mentor-details-date-enrollment'
                              component={FormDateInput}
                              label={`Date of enrollment${isEnrollmentDateRequired(formProps.values.status)
                                  ? '*'
                                  : ''
                                }`}
                              maxDate={new Date()}
                              editable
                              parse={(value) => {
                                try {
                                  if (value) {
                                    return formatDateToRFC(value);
                                  }
                                  return '';
                                } catch (e: any) {
                                  return 'invalid';
                                }
                              }}
                              isReadOnly={
                                !hasAccessToAction('mentor.details.update')
                              }
                            />
                          </FormGroup>
                        )}

                      <FormGroup>
                        <Field<string>
                          name='gender'
                          testid='mentor-details-gender'
                          component={FormSelect}
                          label='Gender'
                          options={GENDER_MENTOR}
                          readOnly={!hasAccessToUpdateAdditionalInfo}
                          parse={parseEmptyString}
                        />
                        <Field<string>
                          name='race'
                          testid='mentor-details-race'
                          component={FormSelect}
                          label='Race'
                          options={RACE}
                          readOnly={!hasAccessToUpdateAdditionalInfo}
                          parse={parseEmptyString}
                        />
                      </FormGroup>
                      {hasAccessToAction(
                        'mentor.details.field.specializations',
                      ) && (
                          <FormGroup>
                            <Field<Specialization['id'][]>
                              name='specializations'
                              data-testid='mentor-details-specializations'
                              component={FormSpecializationInput}
                              label='Specialization'
                              disabled={!mentorSpecializations}
                            />
                          </FormGroup>
                        )}

                      {isMobile() && (
                        <FormGroup className={classes.statusField}>
                          <Field<string>
                            testid='mentor-details-status'
                            name='status'
                            component={FormSelect}
                            label='Status'
                            options={STATUSES_MENTORS}
                            readOnly={
                              !(hasRole(Role.Admin) || hasRole(Role.Manager)) ||
                              pendingStatusChange !== null
                            }
                            parse={(value) =>
                              handleStatusChange(
                                value as MentorStatus,
                                formProps.values.status,
                              )
                            }
                          />
                        </FormGroup>
                      )}

                      <FormGroup id='about'>
                        <Field<string>
                          data-testid='mentor-details-about'
                          name='about'
                          component={TextFieldWrapper}
                          parse={parseEmptyString}
                          label='About*'
                          multiline
                          InputProps={{
                            inputProps: {
                              className: classes.aboutField,
                              maxLength: 8000,
                              readOnly: !hasAccessToUpdateAdditionalInfo,
                            },
                          }}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Field<string>
                          name='currentRole'
                          data-testid='mentor-details-current-role'
                          component={TextFieldWrapper}
                          label='Current Role'
                          parse={parseEmptyString}
                          InputProps={{
                            inputProps: {
                              maxLength: lengthField.currentRole,
                              readOnly: !hasAccessToAction(
                                'mentor.details.update',
                              ),
                            },
                          }}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Field<string>
                          name='companyName'
                          data-testid='mentor-details-company-name'
                          component={TextFieldWrapper}
                          label='Company Name'
                          parse={parseEmptyString}
                          InputProps={{
                            inputProps: {
                              maxLength: lengthField.companyName,
                              readOnly: !hasAccessToAction(
                                'mentor.details.update',
                              ),
                            },
                          }}
                        />
                      </FormGroup>
                      <FormGroup mobile={checkSizeTablet(800)}>
                        <Field<string>
                          name='seniority'
                          testid='mentor-details-seniority'
                          component={FormComboBox}
                          label='Seniority'
                          parse={parseEmptyString}
                          options={[...SENIORITY_OPTIONS]}
                          readOnly={!hasAccessToAction('mentor.details.update')}
                        />
                        <Field<string>
                          name='experienceYears'
                          testid='mentor-details-experience-years'
                          component={NumberField}
                          label='Experience (years)'
                          InputProps={{
                            inputProps: {
                              min: 0,
                              max: 99,
                              readOnly: !hasAccessToAction(
                                'mentor.details.update',
                              ),
                            },
                          }}
                        />
                      </FormGroup>
                      <FormGroup mobile={checkSizeTablet(800)}>
                        <Field<string>
                          name='isEnterpreneur'
                          testid='mentor-details-is-entrepreneur'
                          component={FormSelect}
                          label='Entrepreneur'
                          options={RADIO_SELECT}
                          readOnly={!hasAccessToAction('mentor.details.update')}
                        />
                        <Field<string>
                          name='isInvestor'
                          testid='mentor-details-is-investor'
                          component={FormSelect}
                          label='Investor'
                          options={RADIO_SELECT}
                          readOnly={!hasAccessToAction('mentor.details.update')}
                        />
                        <Field<string>
                          name='mentorCapacity'
                          testid='mentor-details-mentor-capacity'
                          component={NumberField}
                          label={`${rb('mentor-u')} Capacity*`}
                          InputProps={{
                            inputProps: {
                              min: 0,
                              max: 10,
                              readOnly: !hasAccessToAction(
                                'mentor.details.update',
                              ),
                            },
                          }}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Field<string>
                          name='skills'
                          data-testid='mentor-details-skills'
                          component={TextFieldWrapper}
                          label='Skills'
                          parse={parseEmptyString}
                          multiline
                          InputProps={{
                            inputProps: {
                              maxLength: lengthField.skills,
                              readOnly: !hasAccessToAction(
                                'mentor.details.update',
                              ),
                            },
                          }}
                        />
                      </FormGroup>
                    </div>
                    <div className={classes.sectionBlock}>
                      <Typography className={classes.sectionTitle} variant='h3'>
                        Contacts
                      </Typography>
                      {hasAccessToAction('mentor.details.field.email') && (
                        <FormGroup>
                          <Field<string>
                            name='email'
                            data-testid='mentor-details-email'
                            component={FormEmail}
                            label='Email*'
                            formatOnBlur
                            format={(value: string) => {
                              return value ? value.toLowerCase() : value;
                            }}
                            entityType='mentor'
                            InputProps={{
                              inputProps: {
                                maxLength: lengthField.email,
                                readOnly: !hasAccessToAction(
                                  'mentor.details.update',
                                ),
                              },
                            }}
                          />
                        </FormGroup>
                      )}
                      {hasAccessToAction('mentor.details.field.phone') && (
                        <FormGroup>
                          <Field<string>
                            name='phone'
                            data-testid='mentor-details-phone'
                            label='Phone'
                            component={TextFieldWrapper}
                            parse={parseEmptyString}
                            InputProps={{
                              inputProps: {
                                maxLength: lengthField.phone,
                                readOnly: !hasAccessToAction(
                                  'mentor.details.update',
                                ),
                              },
                            }}
                          />
                        </FormGroup>
                      )}
                      {hasAccessToAction('mentor.details.field.linkedIn') && (
                        <FormGroup>
                          <Field<string>
                            name='linkedInProfile'
                            data-testid='mentor-details-linkedin'
                            component={TextFieldWrapper}
                            label='LinkedIn profile'
                            placeholder='https://www.linkedin.com/in/your-profile'
                            parse={parseEmptyString}
                            InputProps={{
                              inputProps: {
                                maxLength: lengthField.linkedIn,
                                readOnly: !hasAccessToAction(
                                  'mentor.details.update',
                                ),
                              },
                            }}
                          />
                        </FormGroup>
                      )}
                    </div>
                    {hasAccessToAction('mentor.details.field.address') && (
                      <div className={classes.sectionBlock}>
                        <Typography
                          className={classes.sectionTitle}
                          variant='h3'
                        >
                          Address
                        </Typography>
                        <FormGroup>
                          <Field<string>
                            name='address'
                            data-testid='mentor-details-address'
                            component={TextFieldWrapper}
                            label='Address'
                            parse={parseEmptyString}
                            InputProps={{
                              inputProps: {
                                maxLength: lengthField.address,
                                readOnly: !hasAccessToAction(
                                  'mentor.details.update',
                                ),
                              },
                            }}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Field<string>
                            name='city'
                            data-testid='mentor-details-city'
                            component={TextFieldWrapper}
                            label='City'
                            parse={parseEmptyString}
                            InputProps={{
                              inputProps: {
                                maxLength: lengthField.city,
                                readOnly: !hasAccessToAction(
                                  'mentor.details.update',
                                ),
                              },
                            }}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Field<string>
                            name='state'
                            data-testid='mentor-details-state'
                            component={TextFieldWrapper}
                            label='State'
                            parse={parseEmptyString}
                            InputProps={{
                              inputProps: {
                                maxLength: lengthField.state,
                                readOnly: !hasAccessToAction(
                                  'mentor.details.update',
                                ),
                              },
                            }}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Field<string>
                            name='zip'
                            data-testid='mentor-details-zip'
                            component={TextFieldWrapper}
                            label='Zip code'
                            parse={parseEmptyString}
                            InputProps={{
                              inputProps: {
                                maxLength: lengthField.zip,
                                readOnly: !hasAccessToAction(
                                  'mentor.details.update',
                                ),
                              },
                            }}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Field<string>
                            name='country'
                            data-testid='mentor-details-country'
                            component={TextFieldWrapper}
                            parse={parseEmptyString}
                            label='Country'
                            InputProps={{
                              inputProps: {
                                maxLength: lengthField.country,
                                readOnly: !hasAccessToAction(
                                  'mentor.details.update',
                                ),
                              },
                            }}
                          />
                        </FormGroup>
                      </div>
                    )}
                    {hasAccessToAction('mentor.details.update') && (
                      <div className={classes.actionsBlock}>
                        <StickyContent>
                          <Button
                            data-testid='mentor-details-button-submit'
                            className={CLASS_TRACKING.INTERNAL_ACTION}
                            onClick={formProps.handleSubmit}
                            disabled={loading}
                            startIcon={<CheckIcon />}
                          >
                            {loading ? (
                              <CircularProgress size={24} color='inherit' />
                            ) : (
                              'Save'
                            )}
                          </Button>
                        </StickyContent>
                      </div>
                    )}
                  </form>
                </div>
                {!isMobile() && (
                  <div className={classes.logoFormBlock}>
                    <Field<string>
                      name='logo'
                      parse={(value) => (!value ? '' : value)}
                      component={({ input }) => (
                        <UserLogo
                          logo={input.value}
                          name={getAbbreviationByName(
                            formProps.values.firstName,
                            formProps.values.lastName,
                          )}
                          onUpload={(photoFile) => {
                            if (validateLogoFile(photoFile)) {
                              setLogoFile(photoFile);
                              const blobFile = URL.createObjectURL(photoFile);
                              input.onChange(blobFile);
                            }
                          }}
                          onRemove={() => {
                            setLogoFile(null);
                            input.onChange('');
                          }}
                          readOnly={!hasAccessToAction('mentor.details.update')}
                        />
                      )}
                    />

                    <FormGroup className={classes.statusField}>
                      <Field<string>
                        testid='mentor-details-status'
                        name='status'
                        component={FormSelect}
                        label='Status'
                        options={STATUSES_MENTORS}
                        readOnly={
                          !(hasRole(Role.Admin) || hasRole(Role.Manager)) ||
                          pendingStatusChange !== null
                        }
                        parse={(value) =>
                          handleStatusChange(
                            value as MentorStatus,
                            formProps.values.status,
                          )
                        }
                      />
                    </FormGroup>
                  </div>
                )}
              </div>
            </FormWrapper>
          );
        }}
      />
      <ActivateConfirmationModal
        open={pendingStatusChange !== null}
        onClose={handleActivateCancel}
        onConfirm={handleActivateConfirm}
      />
    </div>
  );
}

function FormWrapper({
  formProps,
  children,
  formAPIRef,
}: {
  formProps: FormRenderProps<FormValues, FormValues>;
  children: any;
  formAPIRef: React.MutableRefObject<any>;
}) {
  const formAPI = useForm();

  useEffect(() => {
    formAPIRef.current = formAPI;
    formAPI.change('logo', formProps.initialValues.logo);
  }, [formAPI, formProps.initialValues.logo, formAPIRef]);

  return children;
}

export default MentorDetailsForm;
